import { PrinterIcon } from '@heroicons/react/20/solid';
import type { RowData } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { storeLayout } from '../../router';
import { IconButton } from '../buttons';
import { PrintContainer } from '../print-container';
import { PrintTable } from '../printable-table';
import { isStrictColumnDef } from './column';
import type { AppTable } from './features';

type TanStackTablePrintButtonProps<T extends RowData> = {
  table: AppTable<T> | null;
  title: string;
};

/**
 * Component that extracts sorted data and columns from a TanStack table
 * and renders a print button with print preview functionality.
 *
 * Returns null if table is not yet available (first render).
 */
export function TanStackTablePrintButton<T extends RowData>({
  table,
  title,
}: TanStackTablePrintButtonProps<T>) {
  const [printing, setPrinting] = useState(false);
  const { store } = storeLayout.useRouteContext();

  if (!table) {
    return null;
  }

  const sortedData = table.getRowModel().rows.map((row) => row.original);
  const columns = table
    .getVisibleLeafColumns()
    .map((col) => col.columnDef)
    .filter(isStrictColumnDef);

  const header = (
    <div className="mb-4 text-center">
      <div className="text-lg font-bold">{store.name}</div>
      <div className="text-base font-semibold">{title}</div>
      <div className="text-xs text-gray-400">
        Printed: {format(new Date(), 'MM/dd/yyyy, h:mm a')}
      </div>
    </div>
  );

  return (
    <>
      <IconButton tooltip="Print report" onClick={() => setPrinting(true)}>
        <PrinterIcon className="h-5 w-5" aria-hidden="true" />
      </IconButton>
      {printing && (
        <PrintContainer onAfterPrint={() => setPrinting(false)}>
          <PrintTable columns={columns} items={sortedData} header={header} />
        </PrintContainer>
      )}
    </>
  );
}
